import { z } from 'zod';

import { CounterpartySnapshotSchema } from '../counterparties/counterparties.schema.js';
import { RegulatoryIdentitySchema } from '../counterparties/counterparties.schema.js';
import {
  AttachmentAvailabilityStatusSchema,
  InboundReceiptSourceTypeSchema,
  OfficialComparisonReviewStatusSchema,
  OfficialComparisonStatusSchema,
} from '../inbound-receipts/inbound-receipts-types.js';
import {
  ShipmentBuilderInputSchema,
  ShipmentHeaderSchema,
  ShipmentRangeBuilderInputSchema,
  ShipmentReceiverSelectionSchema,
  ShipmentSourceEvidenceSchema,
  ShipmentTargetBuilderInputSchema,
  validateShipmentSourceEvidencePair,
} from '../shipments/shipments-shared.schema.js';
import { IsoDateTimeSchema, RcScopeTypeSchema } from '../shipments/shipments-types.js';
import { CoverShipmentReceiverSelectionSchema } from '../cover-shipments/cover-shipments-shared.schema.js';
import type {
  CancelSourcePreparationRequestRequest,
  CancelSourcePreparationRequestResponse,
  ClaimSourcePreparationRequestRequest,
  ClaimSourcePreparationRequestResponse,
  CoverSourcePreparationRequestDetail,
  CoverSourcePreparationRequestSummary,
  CoverSourcePreparationSources,
  CreateSourcePreparationRequestRequest,
  CreateSourcePreparationRequestResponse,
  GetSourcePreparationRequestRequest,
  GetSourcePreparationRequestResponse,
  ListSourcePreparationRequestsRequest,
  ListSourcePreparationRequestsResponse,
  OpenCoverSourcePreparationRequestRequest,
  OpenCoverSourcePreparationRequestResponse,
  OpenShipmentSourcePreparationRequestRequest,
  OpenShipmentSourcePreparationRequestResponse,
  ReleaseSourcePreparationRequestRequest,
  ReleaseSourcePreparationRequestResponse,
  ResolveSourcePreparationContextRequest,
  ResolveSourcePreparationContextResponse,
  SourcePreparationAttachmentReservation,
  SourcePreparationAttachmentSnapshot,
  SourcePreparationContextAnchors,
  SourcePreparationOwnPurchaseEvidenceSummary,
  SourcePreparationOwnPurchaseFingerprint,
  SourcePreparationPurchaseLineEvidenceSummary,
  SourcePreparationPurchaseLineFingerprint,
  ShipmentSourcePreparationRequestDetail,
  ShipmentSourcePreparationRequestSummary,
  ShipmentSourcePreparationSources,
  SourcePreparationRequestDetail,
  SourcePreparationRequestSummary,
} from './source-preparation.dto.js';
import {
  SourcePreparationRequestStatusSchema,
  SourcePreparationTargetOutboundTypeSchema,
  SourcePreparationTerminalReasonCodeSchema,
} from './source-preparation-types.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const trimmedStringSchema = z.string().trim().min(1);

export const SourcePreparationAttachmentReservationSchema: z.ZodType<SourcePreparationAttachmentReservation> =
  z
    .object({
      reservedBySourcePreparationRequestId: z.uuid().nullable(),
      reservedAt: IsoDateTimeSchema.nullable(),
      reservedByStationId: z.uuid().nullable(),
      reservedByUserId: z.uuid().nullable(),
    })
    .strict();

export const SourcePreparationAttachmentSnapshotSchema: z.ZodType<SourcePreparationAttachmentSnapshot> =
  z
    .object({
      receiptId: z.uuid(),
      materialId: z.uuid(),
      receiverRcScopeType: RcScopeTypeSchema,
      receiverRcScopeId: z.uuid(),
      sourceType: InboundReceiptSourceTypeSchema,
      shipper: CounterpartySnapshotSchema,
      receiver: CounterpartySnapshotSchema,
      shippingReportNumber: trimmedStringSchema,
      receivedWeightLb: nonNegativeNumberSchema,
      paymentWeightLb: nonNegativeNumberSchema.nullable(),
      refundValueCents: nonNegativeIntSchema.nullable(),
      receivedAt: IsoDateTimeSchema,
      internalTransferReference: trimmedStringSchema.nullable(),
      availabilityStatus: AttachmentAvailabilityStatusSchema,
      comparisonStatus: OfficialComparisonStatusSchema,
      reviewStatus: OfficialComparisonReviewStatusSchema,
      confirmedAt: IsoDateTimeSchema,
      consumedByCoverShipmentId: z.uuid().nullable(),
      reservation: SourcePreparationAttachmentReservationSchema.nullable(),
    })
    .strict();

export const SourcePreparationOwnPurchaseEvidenceSummarySchema: z.ZodType<SourcePreparationOwnPurchaseEvidenceSummary> =
  z
    .object({
      physicalNetLb: nonNegativeNumberSchema.nullable(),
      unitCount: nonNegativeIntSchema.nullable(),
    })
    .strict();

export const SourcePreparationPurchaseLineEvidenceSummarySchema: z.ZodType<SourcePreparationPurchaseLineEvidenceSummary> =
  SourcePreparationOwnPurchaseEvidenceSummarySchema;

export const SourcePreparationOwnPurchaseFingerprintSchema: z.ZodType<SourcePreparationOwnPurchaseFingerprint> =
  z
    .object({
      candidateLineItemIds: z.array(z.uuid()),
      selectedLineItemIds: z.array(z.uuid()),
      selectedPurchaseCommittedIds: z.array(z.uuid()),
      candidateLineHash: trimmedStringSchema,
      selectedLineHash: trimmedStringSchema,
      evidenceSummary: SourcePreparationOwnPurchaseEvidenceSummarySchema.nullable(),
    })
    .strict();

export const SourcePreparationPurchaseLineFingerprintSchema: z.ZodType<SourcePreparationPurchaseLineFingerprint> =
  SourcePreparationOwnPurchaseFingerprintSchema;

export const SourcePreparationContextAnchorsSchema: z.ZodType<SourcePreparationContextAnchors> =
  z
    .object({
      storeId: z.uuid(),
      locationId: z.uuid(),
      stationId: z.uuid(),
      rcScopeType: RcScopeTypeSchema,
      rcScopeId: z.uuid(),
      materialId: z.uuid(),
      ...ShipmentSourceEvidenceSchema.shape,
    })
    .strict();

const CoverSourcePreparationRequestSummarySchemaBase = z
  .object({
    requestId: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    targetOutboundType: z.literal('COVER_SHIPMENT'),
    status: SourcePreparationRequestStatusSchema,
    builderInput: ShipmentBuilderInputSchema,
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    attachmentCount: nonNegativeIntSchema,
    attachmentsWeightLb: nonNegativeNumberSchema,
    ownPurchaseWeightLb: nonNegativeNumberSchema,
    submittedAt: IsoDateTimeSchema,
    submittedByUserId: z.uuid().nullable(),
    claimedAt: IsoDateTimeSchema.nullable(),
    claimedByStationId: z.uuid().nullable(),
    materializedCoverShipmentId: z.uuid().nullable(),
    terminalReasonCode: SourcePreparationTerminalReasonCodeSchema.nullable(),
    ...ShipmentSourceEvidenceSchema.shape,
  })
  .strict();

export const CoverSourcePreparationRequestSummarySchema: z.ZodType<CoverSourcePreparationRequestSummary> =
  CoverSourcePreparationRequestSummarySchemaBase;

export const CoverSourcePreparationSourcesSchema: z.ZodType<CoverSourcePreparationSources> =
  z
    .object({
      attachments: z.array(SourcePreparationAttachmentSnapshotSchema),
      ownPurchases: SourcePreparationOwnPurchaseFingerprintSchema.nullable(),
    })
    .strict();

const CoverSourcePreparationRequestDetailSchemaBase =
  CoverSourcePreparationRequestSummarySchemaBase.extend({
    contextAnchors: SourcePreparationContextAnchorsSchema,
    sources: CoverSourcePreparationSourcesSchema,
  }).strict();

export const CoverSourcePreparationRequestDetailSchema: z.ZodType<CoverSourcePreparationRequestDetail> =
  CoverSourcePreparationRequestDetailSchemaBase;

const ShipmentSourcePreparationRequestSummarySchemaBase = z
  .object({
    requestId: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    targetOutboundType: z.literal('SHIPMENT'),
    status: SourcePreparationRequestStatusSchema,
    builderInput: ShipmentBuilderInputSchema,
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    submittedAt: IsoDateTimeSchema,
    submittedByUserId: z.uuid().nullable(),
    claimedAt: IsoDateTimeSchema.nullable(),
    claimedByStationId: z.uuid().nullable(),
    materializedShipmentId: z.uuid().nullable(),
    terminalReasonCode: SourcePreparationTerminalReasonCodeSchema.nullable(),
    ...ShipmentSourceEvidenceSchema.shape,
  })
  .strict();

export const ShipmentSourcePreparationRequestSummarySchema: z.ZodType<ShipmentSourcePreparationRequestSummary> =
  ShipmentSourcePreparationRequestSummarySchemaBase;

export const ShipmentSourcePreparationSourcesSchema: z.ZodType<ShipmentSourcePreparationSources> =
  z
    .object({
      purchaseLines: SourcePreparationPurchaseLineFingerprintSchema,
    })
    .strict();

const ShipmentSourcePreparationRequestDetailSchemaBase =
  ShipmentSourcePreparationRequestSummarySchemaBase.extend({
    contextAnchors: SourcePreparationContextAnchorsSchema,
    sources: ShipmentSourcePreparationSourcesSchema,
  }).strict();

export const ShipmentSourcePreparationRequestDetailSchema: z.ZodType<ShipmentSourcePreparationRequestDetail> =
  ShipmentSourcePreparationRequestDetailSchemaBase;

export const SourcePreparationRequestSummarySchema: z.ZodType<SourcePreparationRequestSummary> =
  z.discriminatedUnion('targetOutboundType', [
    ShipmentSourcePreparationRequestSummarySchemaBase,
    CoverSourcePreparationRequestSummarySchemaBase,
  ]);

export const SourcePreparationRequestDetailSchema: z.ZodType<SourcePreparationRequestDetail> =
  z.discriminatedUnion('targetOutboundType', [
    ShipmentSourcePreparationRequestDetailSchemaBase,
    CoverSourcePreparationRequestDetailSchemaBase,
  ]);

const createCoverSourcePreparationRequestBaseShape = {
  companyId: z.uuid(),
  stationId: z.uuid(),
  targetOutboundType: z.literal('COVER_SHIPMENT'),
  receiver: CoverShipmentReceiverSelectionSchema,
  attachmentReceiptIds: z.array(z.uuid()).nonempty(),
  ...ShipmentSourceEvidenceSchema.shape,
};

const CreateCoverSourcePreparationRangeRequestSchema =
  ShipmentRangeBuilderInputSchema.extend(createCoverSourcePreparationRequestBaseShape).strict();
const CreateCoverSourcePreparationTargetRequestSchema =
  ShipmentTargetBuilderInputSchema.extend(createCoverSourcePreparationRequestBaseShape).strict();

const createShipmentSourcePreparationRequestBaseShape = {
  companyId: z.uuid(),
  stationId: z.uuid(),
  targetOutboundType: z.literal('SHIPMENT'),
  receiver: ShipmentReceiverSelectionSchema,
  ...ShipmentSourceEvidenceSchema.shape,
};

const CreateShipmentSourcePreparationRangeRequestSchema =
  ShipmentRangeBuilderInputSchema.extend(createShipmentSourcePreparationRequestBaseShape).strict();
const CreateShipmentSourcePreparationTargetRequestSchema =
  ShipmentTargetBuilderInputSchema.extend(createShipmentSourcePreparationRequestBaseShape).strict();

export const CreateSourcePreparationRequestRequestSchema: z.ZodType<CreateSourcePreparationRequestRequest> =
  z
    .union([
      CreateShipmentSourcePreparationRangeRequestSchema,
      CreateShipmentSourcePreparationTargetRequestSchema,
      CreateCoverSourcePreparationRangeRequestSchema,
      CreateCoverSourcePreparationTargetRequestSchema,
    ])
    .superRefine((value, ctx) => {
      validateShipmentSourceEvidencePair(value, ctx);
      if (value.targetOutboundType === 'SHIPMENT') {
        return;
      }
      const seen = new Set<string>();
      const duplicates = new Set<string>();
      for (const receiptId of value.attachmentReceiptIds) {
        if (seen.has(receiptId)) {
          duplicates.add(receiptId);
        }
        seen.add(receiptId);
      }
      if (duplicates.size > 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Duplicate attachments are not allowed.',
          path: ['attachmentReceiptIds'],
        });
      }
    });

export const CreateSourcePreparationRequestResponseSchema: z.ZodType<CreateSourcePreparationRequestResponse> =
  z
    .object({
      request: SourcePreparationRequestDetailSchema,
    })
    .strict();

export const ResolveSourcePreparationContextRequestSchema: z.ZodType<ResolveSourcePreparationContextRequest> =
  z
    .object({
      companyId: z.uuid(),
      stationId: z.uuid(),
    })
    .strict();

export const ResolveSourcePreparationContextResponseSchema: z.ZodType<ResolveSourcePreparationContextResponse> =
  z
    .object({
      rcScopeType: RcScopeTypeSchema,
      rcScopeId: z.uuid(),
      regulatoryIdentity: RegulatoryIdentitySchema,
      scopeLabel: trimmedStringSchema,
      timeZone: trimmedStringSchema,
      storeId: z.uuid(),
      locationId: z.uuid(),
      stationId: z.uuid(),
    })
    .strict();

export const ListSourcePreparationRequestsRequestSchema: z.ZodType<ListSourcePreparationRequestsRequest> =
  z
    .object({
      companyId: z.uuid(),
      stationId: z.uuid().optional(),
      targetOutboundType: SourcePreparationTargetOutboundTypeSchema.optional(),
      statuses: z.array(SourcePreparationRequestStatusSchema).optional(),
    })
    .strict();

export const ListSourcePreparationRequestsResponseSchema: z.ZodType<ListSourcePreparationRequestsResponse> =
  z
    .object({
      items: z.array(SourcePreparationRequestSummarySchema),
    })
    .strict();

export const GetSourcePreparationRequestRequestSchema: z.ZodType<GetSourcePreparationRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      requestId: z.uuid(),
    })
    .strict();

export const GetSourcePreparationRequestResponseSchema: z.ZodType<GetSourcePreparationRequestResponse> =
  z
    .object({
      request: SourcePreparationRequestDetailSchema,
    })
    .strict();

export const ClaimSourcePreparationRequestRequestSchema: z.ZodType<ClaimSourcePreparationRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      requestId: z.uuid(),
      stationId: z.uuid(),
    })
    .strict();

export const ClaimSourcePreparationRequestResponseSchema: z.ZodType<ClaimSourcePreparationRequestResponse> =
  z
    .object({
      request: SourcePreparationRequestDetailSchema,
    })
    .strict();

export const CancelSourcePreparationRequestRequestSchema: z.ZodType<CancelSourcePreparationRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      requestId: z.uuid(),
    })
    .strict();

export const CancelSourcePreparationRequestResponseSchema: z.ZodType<CancelSourcePreparationRequestResponse> =
  z
    .object({
      request: SourcePreparationRequestDetailSchema,
    })
    .strict();

export const ReleaseSourcePreparationRequestRequestSchema: z.ZodType<ReleaseSourcePreparationRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      requestId: z.uuid(),
      stationId: z.uuid(),
    })
    .strict();

export const ReleaseSourcePreparationRequestResponseSchema: z.ZodType<ReleaseSourcePreparationRequestResponse> =
  z
    .object({
      request: SourcePreparationRequestDetailSchema,
    })
    .strict();

export const OpenCoverSourcePreparationRequestRequestSchema: z.ZodType<OpenCoverSourcePreparationRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      request: CoverSourcePreparationRequestDetailSchema,
    })
    .strict();

const openCoverSourcePreparationCoverShipmentBaseShape = {
  companyId: z.uuid(),
  stationId: z.uuid(),
  receiver: CoverShipmentReceiverSelectionSchema,
  attachmentReceiptIds: z.array(z.uuid()).nonempty(),
  sourcePreparationRequestId: z.uuid(),
  sourcePreparationSnapshot: CoverSourcePreparationRequestDetailSchema,
  ...ShipmentSourceEvidenceSchema.shape,
};

const OpenCoverSourcePreparationCoverShipmentRangeRequestSchema =
  ShipmentRangeBuilderInputSchema.extend(
    openCoverSourcePreparationCoverShipmentBaseShape,
  ).strict();
const OpenCoverSourcePreparationCoverShipmentTargetRequestSchema =
  ShipmentTargetBuilderInputSchema.extend(
    openCoverSourcePreparationCoverShipmentBaseShape,
  ).strict();

const OpenCoverSourcePreparationCoverShipmentRequestSchema = z
  .discriminatedUnion('mode', [
    OpenCoverSourcePreparationCoverShipmentRangeRequestSchema,
    OpenCoverSourcePreparationCoverShipmentTargetRequestSchema,
  ])
  .superRefine(validateShipmentSourceEvidencePair);

export const OpenCoverSourcePreparationRequestResponseSchema: z.ZodType<OpenCoverSourcePreparationRequestResponse> =
  z
    .object({
      sourcePreparationRequest: CoverSourcePreparationRequestDetailSchema,
      coverShipmentRequest: OpenCoverSourcePreparationCoverShipmentRequestSchema,
      lockedAttachmentReceiptIds: z.array(z.uuid()).nonempty(),
    })
    .strict();

export const OpenShipmentSourcePreparationRequestRequestSchema: z.ZodType<OpenShipmentSourcePreparationRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      request: ShipmentSourcePreparationRequestDetailSchema,
    })
    .strict();

export const OpenShipmentSourcePreparationRequestResponseSchema: z.ZodType<OpenShipmentSourcePreparationRequestResponse> =
  z
    .object({
      shipment: ShipmentHeaderSchema,
    })
    .strict();
